/**
 * Procedural rock/boulder geometry (issue #63): a noise-displaced, welded
 * icosphere driven through a layered radial displacement field — squash,
 * then macro warp, tilted strata banding, ridged creases, planar fracture
 * cuts, and micro grain — so rocks read as irregular, faceted, banded
 * boulders rather than smooth spheres. Per-vertex data (vdata: strataT,
 * moss/lichen openness, cavity AO) drives a UV-less shared material in
 * rocks.ts.
 *
 * Pure and (mostly) three.js-free: only three's Vector3 is used for the
 * radial-direction/cut-plane maths (the same "three.js math classes are fine
 * in generators" precedent issue #58 set for treeSkeleton.ts) — no renderer
 * or DOM dependency, so the whole pipeline is unit-tested in Node.
 *
 * Attribution: displacement field ported from Braffolk/fable5-world-demo
 * `src/vegetation/RockBuilder.ts` (MIT licence, commit fd75fdb) — see
 * outputs/qa/mining/05_visual_quality.md §7. The reference is already plain
 * CPU geometry (no WebGPU dependency), so the maths port over almost
 * verbatim; this version re-expresses the reference's bespoke fbm3/ridged3
 * noise with a 3D extension of this codebase's existing Math.imul
 * lattice-hash idiom (terrain/terrainNoise.ts's hash2/valueNoise2D — that
 * module's noise is a 2D UV-tileable pair baked for the terrain material and
 * doesn't fit a field sampled over unit-sphere directions, so it wasn't
 * reusable directly; the hashing/fbm *construction* is reused, not a new
 * scheme). The reference's Rng class (built to fork many decorrelated
 * streams for tree-skeleton growth) is replaced with the same seeded
 * Math.sin hash01 idiom used throughout vegetationPlacement.ts, since a rock
 * field only ever needs a handful of independent scalar draws.
 */
import { Vector3 } from 'three';

export interface RockParams {
  /** Overall radius (m) before per-instance scale. */
  radius: number;
  /** Ellipsoid squash per axis (y typically < 1). */
  squash: [number, number, number];
  /** Macro shape warp amplitude (fraction of radius). */
  macro: number;
  /** Strata band amplitude (0 disables banding). */
  strata: number;
  strataFreq: number;
  /** Strata axis tilt (radians from up). */
  strataTilt: number;
  /** Ridged crease amplitude. */
  ridged: number;
  /** Number of planar fracture cuts. */
  cuts: number;
  /** Cut depth (how far planes bite, fraction of radius). */
  cutBite: number;
  /** Micro-grain amplitude. */
  micro: number;
}

/** Four presets covering the silhouette variety the AC asks for (irregular,
 * faceted, banded) — a subset of the reference's seven, dropping hero/
 * cliffFace/talus, which exist there for open-world scale diversity that
 * ray2's tens-to-low-hundreds ground scatter doesn't need. */
export const ROCK_PRESETS: Record<string, RockParams> = {
  boulder: {
    radius: 1.1,
    squash: [1, 0.74, 0.92],
    macro: 0.24,
    strata: 0.045,
    strataFreq: 3.2,
    strataTilt: 0.2,
    ridged: 0.05,
    cuts: 2,
    cutBite: 0.1,
    micro: 0.012,
  },
  angular: {
    radius: 0.85,
    squash: [1, 0.85, 0.95],
    macro: 0.22,
    strata: 0.04,
    strataFreq: 4,
    strataTilt: 0.5,
    ridged: 0.12,
    cuts: 10,
    cutBite: 0.5,
    micro: 0.016,
  },
  slab: {
    radius: 1.7,
    squash: [1, 0.42, 0.78],
    macro: 0.16,
    strata: 0.08,
    strataFreq: 5,
    strataTilt: 0.12,
    ridged: 0.05,
    cuts: 5,
    cutBite: 0.28,
    micro: 0.012,
  },
  cobble: {
    radius: 0.16,
    squash: [1, 0.72, 0.88],
    macro: 0.14,
    strata: 0,
    strataFreq: 1,
    strataTilt: 0,
    ridged: 0.02,
    cuts: 0,
    cutBite: 0,
    micro: 0.01,
  },
};

export type RockPreset = keyof typeof ROCK_PRESETS;
export const ROCK_PRESET_NAMES = Object.keys(ROCK_PRESETS) as RockPreset[];

/** Icosphere subdivision levels. NEAR is what rocks.ts currently deploys
 * (visibly faceted up close, still cheap at a tens-to-low-hundreds instance
 * count); FAR exists so this generator supports a future runtime near/far
 * swap (see rocks.ts header for why that swap isn't wired up yet). Both
 * sample the SAME continuous displacement field from one seed, so their
 * gross silhouette matches (tested below) even though vertex density
 * differs. */
export const NEAR_DETAIL = 3; // 642 verts / 1280 tris
export const FAR_DETAIL = 1; // 42 verts / 80 tris

interface CutPlane {
  n: Vector3;
  off: number;
}

/** Deterministic fractional hash in [0, 1) — the same Math.sin idiom used
 * across this codebase's placement/geometry modules. */
function hash01(i: number): number {
  const x = Math.sin(i * 12.9898) * 43758.5453;
  return x - Math.floor(x);
}

/** 32-bit lattice hash → [0,1), the same Math.imul idiom as terrainNoise.ts's
 * hash2, extended to three axes. */
function hash3(ix: number, iy: number, iz: number, seed: number): number {
  let h = (Math.imul(ix, 374761393) + Math.imul(iy, 668265263) + Math.imul(iz, 2147483647) + Math.imul(seed, 1442695040)) | 0;
  h = Math.imul(h ^ (h >>> 13), 1274126177);
  h ^= h >>> 16;
  return (h >>> 0) / 4294967296;
}

function smooth(t: number): number {
  return t * t * (3 - 2 * t);
}

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

/** Trilinear value noise in [0,1) over unbounded 3D space (no tiling needed —
 * this samples a direction field, not a texture). */
function valueNoise3D(x: number, y: number, z: number, seed: number): number {
  const x0 = Math.floor(x);
  const y0 = Math.floor(y);
  const z0 = Math.floor(z);
  const fx = smooth(x - x0);
  const fy = smooth(y - y0);
  const fz = smooth(z - z0);
  const x1 = x0 + 1;
  const y1 = y0 + 1;
  const z1 = z0 + 1;
  const c000 = hash3(x0, y0, z0, seed);
  const c100 = hash3(x1, y0, z0, seed);
  const c010 = hash3(x0, y1, z0, seed);
  const c110 = hash3(x1, y1, z0, seed);
  const c001 = hash3(x0, y0, z1, seed);
  const c101 = hash3(x1, y0, z1, seed);
  const c011 = hash3(x0, y1, z1, seed);
  const c111 = hash3(x1, y1, z1, seed);
  const x00 = lerp(c000, c100, fx);
  const x10 = lerp(c010, c110, fx);
  const x01 = lerp(c001, c101, fx);
  const x11 = lerp(c011, c111, fx);
  const y0i = lerp(x00, x10, fy);
  const y1i = lerp(x01, x11, fy);
  return lerp(y0i, y1i, fz);
}

/** Fractal Brownian value noise, remapped to [-1, 1] (a signed warp, matching
 * the reference's fbm3 usage as a signed displacement). */
function fbm3(x: number, y: number, z: number, seed: number, octaves: number): number {
  let sum = 0;
  let amp = 0.5;
  let ampSum = 0;
  let freq = 1;
  for (let o = 0; o < octaves; o++) {
    sum += amp * valueNoise3D(x * freq, y * freq, z * freq, seed + o * 101);
    ampSum += amp;
    amp *= 0.5;
    freq *= 2;
  }
  return (sum / ampSum) * 2 - 1;
}

/** Ridged fractal noise in [0,1): sharp ridges from folded value noise. */
function ridged3(x: number, y: number, z: number, seed: number, octaves: number): number {
  let sum = 0;
  let amp = 0.5;
  let ampSum = 0;
  let freq = 1;
  for (let o = 0; o < octaves; o++) {
    const n = valueNoise3D(x * freq, y * freq, z * freq, seed + o * 101);
    sum += amp * (1 - Math.abs(2 * n - 1));
    ampSum += amp;
    amp *= 0.5;
    freq *= 2;
  }
  return sum / ampSum;
}

/** Deterministic near-uniform point on the unit sphere from a seed (Marsaglia
 * method over hash01 draws), with the up-axis component scaled by `yBias` —
 * replaces the reference's `rng.gauss()` triple, which only ever fed a
 * `normalize()` afterwards, i.e. only the resulting DIRECTION mattered. */
function hashSphereDir(seed: number, yBias: number): Vector3 {
  const u = hash01(seed * 3.1 + 0.7);
  const v = hash01(seed * 5.9 + 1.3);
  const z = u * 2 - 1;
  const r = Math.sqrt(Math.max(0, 1 - z * z));
  const theta = v * Math.PI * 2;
  return new Vector3(r * Math.cos(theta), z * yBias, r * Math.sin(theta)).normalize();
}

/** Welded icosphere (edge-midpoint cache subdivision), ported verbatim from
 * the reference's `icosphere()`. */
function icosphere(detail: number): { positions: Float32Array; indices: Uint32Array } {
  const t = (1 + Math.sqrt(5)) / 2;
  const verts: number[][] = [
    [-1, t, 0],
    [1, t, 0],
    [-1, -t, 0],
    [1, -t, 0],
    [0, -1, t],
    [0, 1, t],
    [0, -1, -t],
    [0, 1, -t],
    [t, 0, -1],
    [t, 0, 1],
    [-t, 0, -1],
    [-t, 0, 1],
  ].map((v) => {
    const l = Math.hypot(v[0], v[1], v[2]);
    return [v[0] / l, v[1] / l, v[2] / l];
  });
  let faces: number[][] = [
    [0, 11, 5],
    [0, 5, 1],
    [0, 1, 7],
    [0, 7, 10],
    [0, 10, 11],
    [1, 5, 9],
    [5, 11, 4],
    [11, 10, 2],
    [10, 7, 6],
    [7, 1, 8],
    [3, 9, 4],
    [3, 4, 2],
    [3, 2, 6],
    [3, 6, 8],
    [3, 8, 9],
    [4, 9, 5],
    [2, 4, 11],
    [6, 2, 10],
    [8, 6, 7],
    [9, 8, 1],
  ];
  const midCache = new Map<number, number>();
  const midpoint = (a: number, b: number): number => {
    const key = a < b ? a * 16777216 + b : b * 16777216 + a;
    const hit = midCache.get(key);
    if (hit !== undefined) return hit;
    const va = verts[a];
    const vb = verts[b];
    const m = [(va[0] + vb[0]) / 2, (va[1] + vb[1]) / 2, (va[2] + vb[2]) / 2];
    const l = Math.hypot(m[0], m[1], m[2]);
    verts.push([m[0] / l, m[1] / l, m[2] / l]);
    const id = verts.length - 1;
    midCache.set(key, id);
    return id;
  };
  for (let d = 0; d < detail; d++) {
    const next: number[][] = [];
    for (const f of faces) {
      const [a, b, c] = f;
      const ab = midpoint(a, b);
      const bc = midpoint(b, c);
      const ca = midpoint(c, a);
      next.push([a, ab, ca], [b, bc, ab], [c, ca, bc], [ab, bc, ca]);
    }
    faces = next;
  }
  const positions = new Float32Array(verts.length * 3);
  for (let i = 0; i < verts.length; i++) {
    positions[i * 3] = verts[i][0];
    positions[i * 3 + 1] = verts[i][1];
    positions[i * 3 + 2] = verts[i][2];
  }
  const indices = new Uint32Array(faces.length * 3);
  for (let i = 0; i < faces.length; i++) {
    indices[i * 3] = faces[i][0];
    indices[i * 3 + 1] = faces[i][1];
    indices[i * 3 + 2] = faces[i][2];
  }
  return { positions, indices };
}

/** Per-triangle cross-product accumulation + normalize, the same unweighted
 * smooth-normal algorithm three.js's BufferGeometry.computeVertexNormals()
 * uses — implemented directly here so this module stays free of any
 * BufferGeometry/renderer dependency. */
function computeSmoothNormals(positions: Float32Array, indices: Uint32Array): Float32Array {
  const normals = new Float32Array(positions.length);
  const pA = new Vector3();
  const pB = new Vector3();
  const pC = new Vector3();
  const cb = new Vector3();
  const ab = new Vector3();
  for (let i = 0; i < indices.length; i += 3) {
    const ia = indices[i];
    const ib = indices[i + 1];
    const ic = indices[i + 2];
    pA.set(positions[ia * 3], positions[ia * 3 + 1], positions[ia * 3 + 2]);
    pB.set(positions[ib * 3], positions[ib * 3 + 1], positions[ib * 3 + 2]);
    pC.set(positions[ic * 3], positions[ic * 3 + 1], positions[ic * 3 + 2]);
    cb.subVectors(pC, pB);
    ab.subVectors(pA, pB);
    cb.cross(ab);
    normals[ia * 3] += cb.x;
    normals[ia * 3 + 1] += cb.y;
    normals[ia * 3 + 2] += cb.z;
    normals[ib * 3] += cb.x;
    normals[ib * 3 + 1] += cb.y;
    normals[ib * 3 + 2] += cb.z;
    normals[ic * 3] += cb.x;
    normals[ic * 3 + 1] += cb.y;
    normals[ic * 3 + 2] += cb.z;
  }
  for (let i = 0; i < normals.length; i += 3) {
    const len = Math.hypot(normals[i], normals[i + 1], normals[i + 2]) || 1;
    normals[i] /= len;
    normals[i + 1] /= len;
    normals[i + 2] /= len;
  }
  return normals;
}

export interface BuiltRockGeometry {
  positions: Float32Array;
  normals: Float32Array;
  /** Per-vertex [strataT, mossOpenness, cavityAO] triple, flattened
   * (3 floats/vertex) — drives rocks.ts's shared UV-less material. */
  vdata: Float32Array;
  indices: Uint32Array;
  triCount: number;
  /** Lowest Y extent (m, real scale, before any per-instance scale
   * multiplier) — rocks.ts grounds an instance so this point sits exactly on
   * the sampled DTM height (the "contact ring"), not the geometry's
   * origin/centre. */
  minY: number;
}

/**
 * Builds one rock's geometry: a welded icosphere at the given subdivision
 * `detail`, radially displaced by the squash → macro warp → strata banding
 * → ridged creases → planar fracture cuts → micro grain field. Deterministic
 * in (preset, seed, detail).
 */
export function buildRockGeometry(preset: RockPreset, seed: number, detail: number): BuiltRockGeometry {
  const p = ROCK_PRESETS[preset];
  if (!p) throw new Error(`no rock preset "${preset}"`);
  const seedA = Math.floor(hash01(seed * 2.1 + 0.11) * 0x7fffffff);
  const seedB = Math.floor(hash01(seed * 4.3 + 0.37) * 0x7fffffff);
  const seedC = Math.floor(hash01(seed * 6.7 + 0.59) * 0x7fffffff);

  const cuts: CutPlane[] = [];
  for (let i = 0; i < p.cuts; i++) {
    const n = hashSphereDir(seed * 97 + i * 13 + 3, 0.7);
    const off = 1 - p.cutBite * (0.4 + hash01(seed * 101 + i * 17 + 5) * 0.6);
    cuts.push({ n, off });
  }
  const tiltPhi = hash01(seed * 11 + 0.3) * Math.PI * 2;
  const strataAxis = new Vector3(
    Math.sin(p.strataTilt) * Math.cos(tiltPhi),
    Math.cos(p.strataTilt),
    Math.sin(p.strataTilt) * Math.sin(tiltPhi),
  ).normalize();
  const strataPhase = hash01(seed * 13 + 0.7) * 10;
  const bandAmp: number[] = [];
  for (let i = 0; i < 24; i++) bandAmp.push(0.55 + hash01(seed * 19 + i * 3 + 1) * 0.9);

  const { positions: pos, indices } = icosphere(detail);
  const n = pos.length / 3;
  const vdata = new Float32Array(n * 3);
  const dir = new Vector3();
  let minY = Infinity;

  for (let i = 0; i < n; i++) {
    dir.set(pos[i * 3], pos[i * 3 + 1], pos[i * 3 + 2]);

    const macro = fbm3(dir.x * 1.4, dir.y * 1.4, dir.z * 1.4, seedA, 3) * p.macro;
    const s =
      dir.dot(strataAxis) * p.strataFreq +
      strataPhase +
      fbm3(dir.x * 2.3, dir.y * 2.3, dir.z * 2.3, seedB, 2) * 0.5;
    const band = Math.floor(s);
    const f = s - band;
    const amp = bandAmp[((band % 24) + 24) % 24];
    // Ledge profile: quick rise, slow fall → overhang-ish steps.
    const ledge = (Math.min(f * 4.2, 1) - f * 0.62) * p.strata * amp;
    const rid = ridged3(dir.x * 3.1, dir.y * 3.1, dir.z * 3.1, seedC, 3) * p.ridged;
    const micro = fbm3(dir.x * 14, dir.y * 14, dir.z * 14, seedB ^ 0x55aa, 2) * p.micro;
    let r = 1 + macro + ledge + rid + micro;

    // Fracture cuts: clamp the radius against each plane (flat facets),
    // accumulating a cavity/AO signal at the cut edge.
    let cav = 0;
    for (const c of cuts) {
      const dn = dir.dot(c.n);
      if (dn > 0.001) {
        const rCut = c.off / dn;
        if (rCut < r) {
          const depth = Math.min((r - rCut) * 3, 1);
          r = rCut + (r - rCut) * 0.035; // slight rounding on the facet
          cav = Math.max(cav, depth * 0.4);
        }
      }
    }

    const rr = r * p.radius;
    const px = dir.x * rr * p.squash[0];
    const py = dir.y * rr * p.squash[1];
    const pz = dir.z * rr * p.squash[2];
    pos[i * 3] = px;
    pos[i * 3 + 1] = py;
    pos[i * 3 + 2] = pz;
    if (py < minY) minY = py;

    vdata[i * 3] = f; // strataT
    vdata[i * 3 + 1] = Math.max(0, dir.y); // moss/lichen openness proxy (pre-squash "upness")
    vdata[i * 3 + 2] = 1 - Math.min(1, cav + Math.max(0, -macro - ledge) * 2.2) * 0.85; // cavity AO
  }

  const normals = computeSmoothNormals(pos, indices);
  return { positions: pos, normals, vdata, indices, triCount: indices.length / 3, minY };
}
